import { Button, Card } from "@prisma/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Paperclip, Plus, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatBytes } from "../../lib/format";
import { UploadDrawer } from "./UploadDrawer";

type File_ = components["schemas"]["AttachmentOut"];

/** Related Files (plan v2): briefs, storyboards, licences and release forms on a request or job. */
export function RelatedFiles({ entity, canUpload, meta }: { entity: string; canUpload: boolean; meta: { label: string; value: string }[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ["attachments", entity];
  const [open, setOpen] = useState(false);
  const q = useQuery({ queryKey: key, queryFn: () => call<File_[]>(api.GET("/app/v1/attachments", { params: { query: { entity } } })) });
  const rm = useMutation({
    mutationFn: (id: string) => call(api.POST("/app/v1/attachments/{attachment_id}/remove", { params: { path: { attachment_id: id } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-[16px] font-semibold text-ink"><Paperclip aria-hidden="true" className="size-4 text-violet" />{t("assets.related_files")}</h2>
        {canUpload && <Button variant="ghost" className="h-8 px-2" onClick={() => setOpen(true)}><Plus aria-hidden="true" className="size-4" />{t("assets.add_file")}</Button>}
      </div>
      {(q.data ?? []).length === 0 ? (
        <p className="mt-2 text-[13px] text-ink-2">{t("assets.no_files")}</p>
      ) : (
        <ul className="mt-2 space-y-1.5 text-[13px]">
          {(q.data ?? []).map((f) => (
            <li key={f.id} className="flex items-center gap-2">
              <FileText aria-hidden="true" className="size-4 shrink-0 text-ink-2" />
              <a href={f.url} target="_blank" rel="noreferrer noopener" className="min-w-0 flex-1 truncate text-violet-text hover:underline">{f.file_name}</a>
              <span className="shrink-0 text-ink-2">{t(`assets.attachment_${f.kind.toLowerCase()}`)} · {formatBytes(f.file_size)}</span>
              {canUpload && (
                <button type="button" aria-label={t("assets.remove_file", { name: f.file_name })} onClick={() => rm.mutate(f.id)} className="rounded p-0.5 text-ink-2 hover:text-ink"><X aria-hidden="true" className="size-3.5" /></button>
              )}
            </li>
          ))}
        </ul>
      )}
      <UploadDrawer open={open} onClose={() => setOpen(false)} target={{ target_kind: "attachment", target_id: entity }} meta={meta} invalidate={[key]} />
    </Card>
  );
}
